// Estimates WHERE in the streamed transcript the assistant's audio currently
// is. OpenAI Realtime exposes no transcript↔audio timing on any transport, so
// the position is dead-reckoned (the LiveKit Agents approach): budget the
// reveal in estimated syllables against time elapsed since real playout
// started, and each frame reveal only enough text to cover the deficit.
// Syllables — not chars — because chars/sec swings with word length while
// syllables/sec tracks speech; the measured per-language rate then transfers
// across turns with different content.

// Baseline until the first measured turn lands. Higher than true speech rate
// (~4 syllables/s; LiveKit uses 3.83) because the estimator overcounts vs real
// syllables (silent e, digits) — measured English turns land ~5 units/s
// (voice-trace-7369177: 5.0), and seeding slow reads as the caption lagging.
export const CAPTION_SYLLABLES_PER_SECOND_SEED = 4.5
// Constant head start so the text sits a touch ahead of the voice (ChatGPT
// feel); additive, unlike the old ×1.15 rate factor whose early-finish grew
// with turn length. Also absorbs the server-send → client-playout skew of
// `output_audio_buffer.started`. 0.25 in the field still READ as
// neutral-to-behind (a caption dead even with the voice feels late because
// the eye needs the word before the ear confirms it); 0.4 ≈ two units keeps
// it visibly a word ahead.
export const CAPTION_LEAD_SECONDS = 0.4
// Estimated silence the voice inserts after a sentence; debited from the
// budget when the reveal crosses a boundary. The rate meter backs the same
// estimate out of measured audio time, so the two stay consistent.
export const CAPTION_SENTENCE_PAUSE_SECONDS = 0.4
// Reveal-speed ceiling as a multiple of the speech rate: keeps catch-up after
// a transcript-delta stall or a background tab readable instead of popping a
// paragraph at once.
const CAPTION_CATCHUP_FACTOR = 3
// Cumulative sums are INTEGER milliunits so walk comparisons are exact by
// construction. With float sums, a digit costing nominally 1.0 units could
// accumulate to 1.0000000000000004 — over the exactly-1-unit allowance cap —
// and deadlock the reveal for the rest of the window ("evening 22" wedged at
// index 9 in voice-traces 7369177/7369178). Integers cannot drift.
export const CAPTION_UNIT_GRAIN = 1000
// Speech-gate hysteresis on the raw getStats `inbound-rtp.audioLevel`:
// assistant speech sits ~0.05–0.3 raw (see use-voice-audio-level.ts), silence
// near 0. Speaking is entered immediately above HIGH; silent only after the
// level has held at or below LOW for MIN_SILENCE. Field-tuned from the
// per-window `caption_speech_gate_stats` distributions (voice-trace-7369182):
// the voice's pauses drop to ~3e-5 — 250x below LOW, so a low run is genuine
// acoustic silence, not noise — but they are SHORT, mostly 0.12–0.36s, and
// the original 0.25s confirmation missed nearly all of them. 0.12s (3
// samples at the 40ms cadence) catches them; the worst false positive (a
// long plosive closure ~120ms) holds the reveal for one-to-few frames with
// an instant exit on the release burst.
export const CAPTION_GATE_HIGH_LEVEL = 0.02
export const CAPTION_GATE_LOW_LEVEL = 0.008
const CAPTION_GATE_MIN_SILENCE_SECONDS = 0.12
// The regime latch races gate availability at a session's FIRST audio window:
// the gate proves itself only on its first HIGH sample, one-to-few 40ms
// samples AFTER the window opens and the scheduler's first tick latches
// (voice-trace-7369184: buffer started 13.04s, first tick 13.05s, first HIGH
// 13.17s) — so the first window always latched ungated, and a gapless handoff
// chain inherited that latch for the whole turn, running the session's
// longest response with the resync corrector dead and estimated pauses
// double-counted. A gate that becomes available within this grace of the
// window's start upgrades the window to gated: almost no time or text has
// accrued yet, so the ungated ticks already on the books (skew counted as
// speech, no pauses crossed) mean the same thing under both regimes.
export const CAPTION_GATE_GRACE_SECONDS = 1
// Silence-onset resync: a measured silence means the voice is resting AT a
// pause point in the text, so the estimated position snaps to the nearest one.
// This is the mid-turn drift corrector: a rate error accrues ~a unit per
// sentence, and in a gated window it otherwise compounds for the whole turn —
// the freeze (correctly) removed the pause slack that used to let a lagging
// caption catch back up (voice-trace-7369183: a 29s turn at a cold-start
// bucket rate ran 7 syllables behind with 11 detected pauses that were each a
// wasted resync opportunity). MAX is the FORWARD trust radius: a rest-at-stop
// premise only holds when a stop is actually within reach, so when the nearest
// forward stop is beyond MAX the resync is SKIPPED, not clamped. A far-forward
// stop means the silence is not a sentence pause but a filler→tool gap or the
// end of a short leading item — items are joined with no separator, so the
// first real stop can sit deep in the next item's still-unspoken text.
// Clamping such a snap to MAX and applying it teleported the caption a full
// sentence into unspoken text, and it never recovered (voice-trace-7369204: a
// +2.5s clamped snap at the filler boundary ran the reveal a sentence ahead
// for the rest of the turn). MIN_SPEECH keeps the start-of-window skew silence
// (level feed lags the `started` anchor) from snapping forward through text
// the voice hasn't begun speaking.
const CAPTION_RESYNC_MAX_SECONDS = 2.5
// Forward-resync plausibility bound: the largest rate error a forward snap
// may claim, as a fraction of the paced rate over the speech time since the
// last APPLIED correction. A forward snap of D units after S speech seconds
// asserts the voice ran D/S units/s faster than paced; bucket-vs-turn error
// tops out around ~15% in the field (voice-trace-7369273 session: turn rates
// 3.99–5.57 against buckets 4.4–5.0), so a bigger claim is not a lag rescue
// — it's the wrong-direction geometry above (a LEADING estimate pins the
// next stop ahead) and is skipped. The budget is time-scaled and the
// interval resets only when a FORWARD correction applies, so cumulative
// forward correction over a window is bounded by this fraction of total
// speech time: steady legitimate rescue always fits, runaway same-direction
// correction cannot (7369273's second +9.5-unit snap implied 24% and dies
// here). Skips leave the interval running, so a genuinely extreme lag still
// corrects once enough speech time makes its claim plausible. Backward
// corrections are exempt — they only crawl, they are the anti-"noticeably
// ahead" corrector, and they must NOT reset this interval (they validate no
// forward claim; when they did reset it, a lagging turn's wrong-side crawls
// starved its own rescues — voice-trace-7369274).
const CAPTION_RESYNC_MAX_RATE_DRIFT = 0.15
// Backward corrections are applied as a temporary rate DERATE (a crawl), not
// a target snap: the reveal can't un-show text, so a snapped-back target just
// freezes the caption until the clock re-earns the difference
// (voice-trace-7369185: -8-unit snaps read as ~2s dead stops). Instead the
// target keeps advancing at DERATE_FACTOR × rate until the excess is repaid —
// visibly alive, converging on the voice. The crawl removes the freeze risk
// that forced the original 0.75s backward cap, and that tight cap was itself
// a bug: a capped correction yields no rate evidence, so a too-fast paced
// rate could ratchet — the loop could learn "faster" but never "slower"
// (voice-trace-7369186: both backward snaps capped at exactly 0.75s, the
// poisoned 5.29 rate held for 26s against a measured 4.74, and the caption
// ran ~2s ahead). Still capped, just looser: a wrong backward pin now costs
// a few seconds of visible crawl, and drift past this size means something
// upstream is broken enough that crawling further wouldn't be right either.
const CAPTION_RESYNC_MAX_BACK_SECONDS = 1.5
// Fraction of the paced rate the target advances at while repaying a
// backward correction. 0.5 reads as a deliberate slow-down, not a stall, and
// repays a correction of D seconds over 2×D seconds of speech.
const CAPTION_RESYNC_DERATE_FACTOR = 0.5
const CAPTION_RESYNC_MIN_SPEECH_SECONDS = 1
// A zero-reveal gap this long while the voice is (as far as the gate knows)
// speaking with text still queued counts as a stall. Landings measure
// POSITION error only — the round-8 freezes had healthy landings and were
// caught by feel ("pauses too long before a syllable"); the stall/burst
// counters are the velocity metrics that make that failure measurable.
const CAPTION_STALL_SECONDS = 0.5

// One unit per char: syllable-like speech units. Precision doesn't matter —
// systematic bias is absorbed by the measured per-language rate — content
// independence does.
// Hiragana/Katakana, Hangul syllables+jamo, Han (+ compat): ~1 unit per char.
const CJK_CHAR =
  /[\u3040-\u30ff\u3130-\u318f\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7a3\uf900-\ufaff]/
const VOWEL = /[aeiouyàáâäãåæèéêëìíîïòóôöõøœùúûüýÿаеёиоуыэюяαεηιουω]/i
const WORD_CHAR = /[\p{L}\p{N}]/u
const DIGIT = /[0-9]/
const SENTENCE_END = /[.!?…。！？]/
// Intra-sentence marks the voice also pauses at — resync anchor candidates
// only, NOT sentence-pause debits. Denser candidates keep the nearest-stop
// snap's worst-case error at half the gap between adjacent stops. Includes
// the fullwidth/ideographic comma-class marks (，、；：) — without them ja/zh
// anchors quantize to full sentences exactly where the syllable estimator is
// weakest.
const RESYNC_STOP = /[,;:—–，、；：]/

interface CaptionTextScan {
  // cumulativeMilliunits[i] = speech milliunits (units × 1000, integer) in
  // text[0..i); length text.length + 1.
  cumulativeMilliunits: number[]
  // cumulativePauses[i] = sentence boundaries strictly before index i.
  cumulativePauses: number[]
  // Sorted cumulative milliunits at each resync stop (measured just past the
  // stop char): the positions the voice can plausibly be resting at when the
  // speech gate reports a silence.
  stopMilliunits: number[]
  // Char index of each resync stop, and of the sentence ends among them (a
  // subset). A pause resume backs its cut up to one of these so the voice
  // restarts at a boundary it would have paused at anyway.
  stopIndices: number[]
  sentenceEndIndices: number[]
}

// Units for a run of non-CJK word chars: vowel clusters + one per digit, and
// at least one for any run containing a letter ("hmm").
function wordRunUnits(text: string, start: number, end: number) {
  let units = 0
  let inVowelCluster = false
  let hasLetter = false
  for (let i = start; i < end; i++) {
    const char = text[i]
    if (DIGIT.test(char)) {
      units += 1
      inVowelCluster = false
      continue
    }
    hasLetter = true
    if (VOWEL.test(char)) {
      if (!inVowelCluster) units += 1
      inVowelCluster = true
    } else {
      inVowelCluster = false
    }
  }
  if (hasLetter && units === 0) units = 1
  return units
}

// A word-run char: a word char that isn't CJK. CJK is one unit per char and
// never joins a run, so it can't extend one.
function isWordRunChar(char: string) {
  return WORD_CHAR.test(char) && !CJK_CHAR.test(char)
}

// Where a rescan may safely resume when `text` extends a previously scanned
// prefix. Every index before the result is identical under any suffix — only
// the tail is unstable: the final char (whose sentence-boundary lookahead was
// empty until now, e.g. "3." → "3.5") and a trailing word run (which can still
// grow and re-spread its units, e.g. "hel" → "hello"). Backing up to the run's
// start makes the resume point begin a fresh run and a fresh punctuation run,
// so the scan below never lands mid-token.
function safeResumeIndex(text: string): number {
  const length = text.length
  if (length === 0) return 0
  let index = length
  while (index > 0 && isWordRunChar(text[index - 1])) index--
  // No trailing word run: only the final char's lookahead was empty.
  if (index === length) index = length - 1
  return index
}

// Incremental scanner behind `scanCaptionText`. `update(text)` rescans only the
// unstable tail when the text extends what was scanned before (the streaming
// common case) and falls back to a full scan on any rewrite; the stable
// prefix's cumulative sums and stops stay in place. Append-only streaming
// costs O(1) amortized per character instead of re-scanning the whole turn
// (O(n²) over a turn) on every transcript delta.
export class CaptionTextScanner {
  private text = ""
  readonly cumulativeMilliunits: number[] = [0]
  readonly cumulativePauses: number[] = [0]
  // Stop positions parallel to stopMilliunits. Needed to truncate the tail
  // correctly (stop chars have weight 0, so a milliunit value alone can't tell
  // a prefix stop from one about to be rescanned), and read by the pause cut
  // to find the boundary a resume should restart from.
  readonly stopIndices: number[] = []
  readonly stopMilliunits: number[] = []
  // Sentence-end boundaries only (a subset of the stops), as char index plus
  // cumulative milliunits just past the mark. The scheduler's boundary hold
  // keys off SENTENCE ends specifically — holding at every comma-class stop
  // would pin the caption dead-even at each clause and erase the lead.
  readonly sentenceEndIndices: number[] = []
  readonly sentenceEndMilliunits: number[] = []

  get scannedLength() {
    return this.text.length
  }

  update(text: string) {
    if (text === this.text) return
    const from =
      this.text.length > 0 && text.startsWith(this.text)
        ? safeResumeIndex(this.text)
        : 0
    // Drop the prefix's unstable tail; everything below is recomputed.
    this.cumulativeMilliunits.length = from + 1
    this.cumulativePauses.length = from + 1
    while (
      this.stopIndices.length > 0 &&
      this.stopIndices[this.stopIndices.length - 1] >= from
    ) {
      this.stopIndices.pop()
      this.stopMilliunits.pop()
    }
    while (
      this.sentenceEndIndices.length > 0 &&
      this.sentenceEndIndices[this.sentenceEndIndices.length - 1] >= from
    ) {
      this.sentenceEndIndices.pop()
      this.sentenceEndMilliunits.pop()
    }

    let cumulativeMilli = this.cumulativeMilliunits[from]
    let cumulativePause = this.cumulativePauses[from]
    let index = from
    while (index < text.length) {
      const char = text[index]
      if (CJK_CHAR.test(char)) {
        cumulativeMilli += CAPTION_UNIT_GRAIN
        this.cumulativeMilliunits[index + 1] = cumulativeMilli
        this.cumulativePauses[index + 1] = cumulativePause
        index += 1
        continue
      }
      if (WORD_CHAR.test(char)) {
        let end = index
        while (end < text.length && isWordRunChar(text[end])) end++
        // Spread the run's units across its chars (smooth typewriter instead of
        // popping syllable-sized chunks); the remainder goes to the first chars
        // so the total stays exactly units × GRAIN.
        const length = end - index
        const totalMilli = wordRunUnits(text, index, end) * CAPTION_UNIT_GRAIN
        const perChar = Math.floor(totalMilli / length)
        const remainder = totalMilli % length
        for (let i = index; i < end; i++) {
          cumulativeMilli += perChar + (i - index < remainder ? 1 : 0)
          this.cumulativeMilliunits[i + 1] = cumulativeMilli
          this.cumulativePauses[i + 1] = cumulativePause
        }
        index = end
        continue
      }
      // Sentence boundary: a terminal mark not continuing a punctuation run
      // ("…" / "?!" count once) and not mid-token ("3.5", "U.S"). A following
      // CJK char is NOT mid-token — unspaced ja/zh text puts a letter right
      // after every 。、， and those are real boundaries, not "1,000"s.
      const midToken =
        index + 1 < text.length &&
        WORD_CHAR.test(text[index + 1]) &&
        !CJK_CHAR.test(text[index + 1])
      const isSentenceEnd =
        SENTENCE_END.test(char) &&
        (index === 0 || !SENTENCE_END.test(text[index - 1])) &&
        !midToken
      // Not mid-token keeps "1,000" and "9–5" from anchoring a resync.
      const isStop = isSentenceEnd || (RESYNC_STOP.test(char) && !midToken)
      if (isSentenceEnd) {
        cumulativePause += 1
        this.sentenceEndIndices.push(index)
        this.sentenceEndMilliunits.push(cumulativeMilli)
      }
      // Stop/boundary chars carry no speech units, so the milliunit sum is
      // unchanged across them.
      this.cumulativeMilliunits[index + 1] = cumulativeMilli
      this.cumulativePauses[index + 1] = cumulativePause
      if (isStop) {
        this.stopIndices.push(index)
        this.stopMilliunits.push(cumulativeMilli)
      }
      index += 1
    }
    this.text = text
  }
}

export function scanCaptionText(text: string): CaptionTextScan {
  const scanner = new CaptionTextScanner()
  scanner.update(text)
  return {
    cumulativeMilliunits: scanner.cumulativeMilliunits,
    cumulativePauses: scanner.cumulativePauses,
    stopMilliunits: scanner.stopMilliunits,
    stopIndices: scanner.stopIndices,
    sentenceEndIndices: scanner.sentenceEndIndices,
  }
}

export function captionSyllableCount(text: string) {
  const scan = scanCaptionText(text)
  return scan.cumulativeMilliunits[text.length] / CAPTION_UNIT_GRAIN
}

// Sentence pauses the voice actually plays WITHIN a turn's audio: a trailing
// terminal mark has no audio after it, so it doesn't count. This is what the
// rate meter backs out of the measured window (the scheduler debits the same
// boundaries as the reveal crosses them).
export function captionInternalPauseCount(text: string) {
  const scan = scanCaptionText(text)
  const total = scan.cumulativePauses[text.length]
  const trimmed = text.trimEnd()
  const endsOnBoundary =
    trimmed.length > 0 && SENTENCE_END.test(trimmed[trimmed.length - 1])
  return Math.max(0, endsOnBoundary ? total - 1 : total)
}

type CaptionSpeechGateState = "speaking" | "silent" | "unavailable"

// Hysteresis gate over the assistant's playback level, the Phase 2 refinement:
// while it reports `silent` the pacer's clock freezes instead of estimating
// sentence pauses, so real pause lengths stop drifting the caption. The signal
// is the 25Hz getStats sample the voice orb already uses — NEVER Web Audio on
// the remote stream, which disables Chrome's echo canceller and makes the
// assistant interrupt itself (voice-chat-rtc.ts `sampleAudioLevels`).
//
// `unavailable` until the level ever crosses HIGH: some browsers report no
// inbound audioLevel at all, and every consumer must degrade to the ungated
// (Phase 1) path for those. Availability is sticky for the gate's lifetime
// (one per session); consumers latch gatedness per audio window so the rate
// measurement and the reveal clock always count time under the same regime.
export class CaptionSpeechGate {
  private gateState: CaptionSpeechGateState = "unavailable"
  private silenceSeconds = 0

  get state(): CaptionSpeechGateState {
    return this.gateState
  }

  get available(): boolean {
    return this.gateState !== "unavailable"
  }

  // A new audio window starts as `speaking` (when metered): the level feed
  // lags the `output_audio_buffer.started` anchor by the network/jitter skew,
  // and starting silent would freeze the caption until the first HIGH sample —
  // the stall failure mode. Counting the skew as speech is the Phase 1 bias,
  // absorbed by the measured rate.
  windowStarted() {
    this.silenceSeconds = 0
    if (this.gateState === "silent") {
      this.gateState = "speaking"
    }
  }

  sample({
    level,
    dtSeconds,
  }: {
    level: number | null
    dtSeconds: number
  }): CaptionSpeechGateState {
    if (this.gateState === "unavailable") {
      // Only a real speech-level sample proves the browser meters playback.
      if (level !== null && level >= CAPTION_GATE_HIGH_LEVEL) {
        this.gateState = "speaking"
        this.silenceSeconds = 0
      }
      return this.gateState
    }
    // A null sample mid-session (getStats hiccup) is absence of evidence, not
    // silence: fail open to `speaking` so a broken feed can't freeze the
    // caption.
    if (level === null || level >= CAPTION_GATE_HIGH_LEVEL) {
      this.gateState = "speaking"
      this.silenceSeconds = 0
      return this.gateState
    }
    if (level > CAPTION_GATE_LOW_LEVEL) {
      // Hysteresis band: quiet audio. Not silence evidence (reset the timer),
      // but not loud enough to exit an established silence either.
      this.silenceSeconds = 0
      return this.gateState
    }
    this.silenceSeconds += dtSeconds
    if (this.silenceSeconds >= CAPTION_GATE_MIN_SILENCE_SECONDS) {
      this.gateState = "silent"
    }
    return this.gateState
  }
}

interface CaptionPaceTick {
  dtSeconds: number
  // The audible response id. A key change without the scheduler being torn
  // down is a gapless handoff: the audio buffer never drained between two
  // responses, so the clock keeps running and the predecessor's revealed text
  // carries over as already-spoken units.
  windowKey: string
  // Concatenated text/revealed of the audible response's assistant items, in
  // stream order (reveal fills them in the same order).
  text: string
  revealedLength: number
  syllablesPerSecond: number
  // The speech gate's view of the assistant playback: true/false when the
  // browser meters the inbound level, null when it doesn't
  // (`CaptionSpeechGate` unavailable).
  speaking: boolean | null
  // The engine's per-audio-window regime latch (`audibleWindowGated`,
  // including its grace upgrade). The engine is the SINGLE owner of the
  // latch: the rate meter measures speaking-seconds under it, and the
  // scheduler must freeze/debit/resync under the SAME regime or the rate
  // driving the reveal was measured on a different clock than the one
  // spending it. The scheduler follows this value instead of latching its
  // own copy; it can flip false→true mid-window (the engine's grace upgrade
  // arriving as a prop change).
  windowGated: boolean
}

// One scheduler per audio window (`output_audio_buffer.started` → buffer
// drained). Revealed units and pauses are re-derived from the caller's
// revealedLength every tick rather than tracked incrementally, so external
// changes to the reveal — persist rewrites, snap corrections — self-correct
// instead of compounding.
export class CaptionRevealScheduler {
  private windowKey: string | null = null
  private windowGated = false
  // Sentence pauses debited as ESTIMATES from the target. Ungated windows
  // debit every crossed boundary (the clock runs through real pauses); a
  // gated window's frozen clock measures pauses instead, so this freezes at
  // whatever was already debited when the regime became gated — dropping
  // those debits at a mid-window grace upgrade would jump the target forward
  // by 0.4s × rate per crossed boundary in a single tick.
  private gatedDebitPauses = 0
  private scanner = new CaptionTextScanner()
  private targetUnits: number | null = null
  private carryUnits = 0
  private carryPauses = 0
  private lastRevealedLength = 0
  private lastSpeaking: boolean | null = null
  // Speech-clock seconds this window has accumulated; gates the resync so the
  // start-of-window skew silence can't anchor before the voice has spoken.
  private speechSeconds = 0
  private resyncCount = 0
  private lastResyncUnits = 0
  // Speech-clock stamp of the last APPLIED FORWARD correction — the forward
  // plausibility interval (see CAPTION_RESYNC_MAX_RATE_DRIFT) runs from
  // here. Backward crawls deliberately leave it alone.
  private lastResyncSpeechSeconds = 0
  private forwardResyncUnits = 0
  private resyncSkipCount = 0
  private lastPacedRate = 0
  // Velocity counters (see CAPTION_STALL_SECONDS): reveal smoothness over
  // the scheduler's life, surfaced in the heartbeat trace and the per-window
  // analytics summary.
  private stallSeconds = 0
  private stallCount = 0
  private maxStallSeconds = 0
  private maxBurstChars = 0
  // Outstanding backward correction, repaid as a crawl: while positive the
  // target accrues at DERATE_FACTOR × rate and the withheld remainder drains
  // this balance (see CAPTION_RESYNC_MAX_BACK_SECONDS for why backward
  // corrections never snap the target directly).
  private derateUnits = 0
  // Reveal-speed budget with carryover: a 60fps frame's budget
  // (dt × rate × CATCHUP) is smaller than one char of a short word, so unspent
  // budget must accumulate or heavy chars never reveal. The one-unit cap keeps
  // catch-up bounded (a single char never costs more than one unit, and the
  // cap converts to exactly 1000 milliunits, so progress is always possible);
  // a single large dt (background tab) still passes through whole so the
  // refocus frame covers the gap at once.
  private allowanceUnits = 0

  tick(tick: CaptionPaceTick): number {
    const revealedChars = this.paceTick(tick)
    // Stall/burst accounting on the way out: a stall is elapsed real time
    // with text queued, the voice not measurably silent, and nothing
    // revealing — the reads-frozen failure the landing metrics can't see.
    if (revealedChars > 0) {
      this.maxBurstChars = Math.max(this.maxBurstChars, revealedChars)
      if (this.stallSeconds >= CAPTION_STALL_SECONDS) {
        this.stallCount += 1
        this.maxStallSeconds = Math.max(this.maxStallSeconds, this.stallSeconds)
      }
      this.stallSeconds = 0
    } else if (
      tick.speaking !== false &&
      tick.revealedLength < tick.text.length
    ) {
      this.stallSeconds += Number.isFinite(tick.dtSeconds)
        ? Math.max(0, tick.dtSeconds)
        : 0
    }
    return revealedChars
  }

  private paceTick(tick: CaptionPaceTick): number {
    const rate = tick.syllablesPerSecond
    // A single bad input would poison the accumulated target FOREVER
    // (NaN += anything stays NaN) and freeze the caption for the rest of the
    // window — refuse the tick instead.
    if (!Number.isFinite(rate) || rate <= 0) return 0
    const rawDtSeconds = Number.isFinite(tick.dtSeconds)
      ? Math.max(0, tick.dtSeconds)
      : 0
    let dtSeconds = rawDtSeconds
    if (this.targetUnits === null || !Number.isFinite(this.targetUnits)) {
      this.targetUnits = CAPTION_LEAD_SECONDS * rate
      this.windowKey = tick.windowKey
      this.windowGated = tick.windowGated
    } else if (!this.windowGated && tick.windowGated) {
      // The engine's grace upgrade (see CAPTION_GATE_GRACE_SECONDS) arriving
      // as a prop change. Freeze the pause debits already on the books: the
      // frozen clock measures every pause from here on, but the boundaries
      // crossed under the ungated regime were debited as estimates and those
      // debits still live inside the target.
      this.windowGated = true
      this.gatedDebitPauses =
        this.carryPauses +
        this.scanner.cumulativePauses[this.lastRevealedLength]
    }
    const silenceOnset =
      this.windowGated && tick.speaking === false && this.lastSpeaking === true
    this.lastSpeaking = tick.speaking
    // Gated window in a measured silence: the clock freezes instead of
    // estimating the pause. Existing deficit may still reveal below.
    if (this.windowGated && tick.speaking === false) {
      dtSeconds = 0
    }

    if (tick.windowKey !== this.windowKey) {
      // Carry the predecessor's FULL text, not the last tick's revealed
      // position: the slice only hands the audible id to a gapless successor
      // once the predecessor is fully revealed, so when no tick ran between
      // the snap-to-full and the key change (background tab, long frame) the
      // stale revealed position undercounts and the phantom clamp below
      // erases legitimately earned catch-up. Self-consistent because carry
      // appears on BOTH sides of the deficit — target clamp and revealed
      // units — so inflating both leaves the deficit unchanged and only the
      // clamp bound loosens to the true upper bound.
      const spoken = this.scanner.scannedLength
      this.carryUnits +=
        this.scanner.cumulativeMilliunits[spoken] / CAPTION_UNIT_GRAIN
      this.carryPauses += this.scanner.cumulativePauses[spoken]
      this.windowKey = tick.windowKey
      this.scanner.update("")
      // A raced first window must not drag a whole gapless chain ungated
      // (voice-trace-7369184 ran its 656-char narration with the resync dead
      // this way) — the engine's latch is authoritative here as everywhere.
      this.windowGated = tick.windowGated
      // At the handoff instant the voice has spoken AT MOST all of the
      // predecessor's text, so any target past carry + lead is phantom — a
      // fast-paced window that ran off the end of its own text. Carrying the
      // phantom poisons the successor: voice-trace-7369186's 5.5s filler
      // overshot by ~5 units, which pushed the successor's first resync onto
      // the WRONG stop, and that wrong pin fed the closed loop a 5.39-unit/s
      // segment the sanity band couldn't catch. Pause debits the target is
      // still carrying (every crossed boundary when ungated, the pre-upgrade
      // snapshot when gated) stay as headroom — they live inside the target
      // and are subtracted at reveal time, not spoken text.
      const pauseHeadroomUnits =
        (this.windowGated ? this.gatedDebitPauses : this.carryPauses) *
        CAPTION_SENTENCE_PAUSE_SECONDS *
        rate
      this.targetUnits = Math.min(
        this.targetUnits,
        this.carryUnits + pauseHeadroomUnits + CAPTION_LEAD_SECONDS * rate
      )
      // The clamp just erased whatever excess a pending crawl was repaying.
      this.derateUnits = 0
    }

    this.scanner.update(tick.text)

    // Gated and ungated windows pace at the measured cross-turn bucket rate.
    const pacedRate = rate
    this.lastPacedRate = pacedRate

    // A pending backward correction repays as a crawl: the target advances
    // at the derated rate and the withheld remainder drains the balance.
    // Repayment is speech-clock time only (dtSeconds is already 0 in a gated
    // silence — the freeze accounts for the pause on its own).
    let accrualUnits = dtSeconds * pacedRate
    if (this.derateUnits > 0 && accrualUnits > 0) {
      const repaid = Math.min(
        accrualUnits * (1 - CAPTION_RESYNC_DERATE_FACTOR),
        this.derateUnits
      )
      accrualUnits -= repaid
      this.derateUnits -= repaid
    }
    this.targetUnits += accrualUnits
    this.speechSeconds += dtSeconds
    if (
      silenceOnset &&
      this.speechSeconds >= CAPTION_RESYNC_MIN_SPEECH_SECONDS
    ) {
      const deltaUnits = this.resyncDelta(this.targetUnits, pacedRate)
      if (
        deltaUnits !== null &&
        deltaUnits >
          CAPTION_RESYNC_MAX_RATE_DRIFT *
            (this.speechSeconds - this.lastResyncSpeechSeconds) *
            pacedRate
      ) {
        // A forward snap claiming a bigger rate error than the drift bound
        // allows for the elapsed interval: skipped, and the interval keeps
        // running so the budget keeps growing (see
        // CAPTION_RESYNC_MAX_RATE_DRIFT).
        this.resyncSkipCount += 1
      } else if (deltaUnits !== null) {
        if (deltaUnits >= 0) {
          // Forward: snap outright (rescuing lag is the whole point), and a
          // forward verdict says any pending crawl was repaying a debt the
          // voice has since disproved. Only a forward verdict resets the
          // drift-bound anchor: a backward crawl validates no forward claim,
          // and letting it refresh forward trust starved legitimate rescues
          // — voice-trace-7369274's lagging turn applied four wrong-side
          // backward crawls whose resets kept the forward budget too small
          // for the two rescues the turn needed (both skipped).
          this.targetUnits += deltaUnits
          this.derateUnits = 0
          this.forwardResyncUnits += deltaUnits
          this.lastResyncSpeechSeconds = this.speechSeconds
        } else {
          // Backward: leave the target and install the crawl. Replace — the
          // delta is measured from the current target, so it already
          // includes whatever a previous correction hadn't repaid.
          this.derateUnits = -deltaUnits
        }
        this.resyncCount += 1
        this.lastResyncUnits = deltaUnits
      }
    }
    // The reveal-speed budget grows from RAW frame time: during a frozen
    // clock (dtSeconds 0) a resynced target must still be able to drain at
    // catch-up speed — that drain happening while the audio is quiet is the
    // whole point of the resync.
    const frameBudget = rawDtSeconds * pacedRate * CAPTION_CATCHUP_FACTOR
    this.allowanceUnits = Math.min(
      this.allowanceUnits + frameBudget,
      Math.max(1, frameBudget)
    )

    const revealed = Math.min(
      Math.max(tick.revealedLength, 0),
      tick.text.length
    )
    this.lastRevealedLength = revealed
    const revealedUnits =
      this.carryUnits +
      this.scanner.cumulativeMilliunits[revealed] / CAPTION_UNIT_GRAIN
    // Gated windows measure silence via the frozen clock, so estimating it
    // again here would double-count every pause and run the caption behind
    // (the exact bug the rate meter's speech-only measurement fixed for the
    // ungated path in voice-trace-7369179) — only the debits frozen at the
    // grace upgrade remain (see gatedDebitPauses).
    const debitedPauses = this.windowGated
      ? this.gatedDebitPauses
      : this.carryPauses + this.scanner.cumulativePauses[revealed]
    const pauseUnits =
      debitedPauses * CAPTION_SENTENCE_PAUSE_SECONDS * pacedRate
    let deficit = this.targetUnits - revealedUnits - pauseUnits
    // The lead is a speech-time affordance — the eye needs the word just
    // before the ear hears it, and during a measured silence nothing is being
    // spoken for it to precede. Holding it back (plus whatever overshoot a
    // crawl is still repaying) keeps a queued backlog from draining lead-first
    // INTO the pause; the withheld portion re-spends the instant the gate
    // hears speech again. A forward-resynced target still drains: the resync
    // leaves the target at stop + lead, so the clamped deficit reaches
    // exactly the stop the voice is resting at.
    if (this.windowGated && tick.speaking === false) {
      deficit -= CAPTION_LEAD_SECONDS * pacedRate + this.derateUnits
    }
    if (deficit <= 0 || revealed >= tick.text.length) return 0

    const budget = Math.min(deficit, this.allowanceUnits)
    if (budget <= 0) return 0

    // Walk in integer milliunits against the scaled budget: at the allowance
    // cap the budget converts to exactly 1000, and no single char exceeds
    // 1000, so the walk can always make progress. Rounded because the budget
    // carries float terms (lead × rate, and the silence clamp above): a
    // 249.9999-milli budget must not wedge forever under a 250-milli char.
    const budgetMilli = Math.round(budget * CAPTION_UNIT_GRAIN)
    const base = this.scanner.cumulativeMilliunits[revealed]
    const limit = this.windowGated
      ? Math.min(
          tick.text.length,
          this.boundaryHoldFrontier(this.targetUnits, pauseUnits, pacedRate)
        )
      : tick.text.length
    let next = revealed
    while (
      next < limit &&
      this.scanner.cumulativeMilliunits[next + 1] - base <= budgetMilli
    ) {
      next++
    }
    this.allowanceUnits = Math.max(
      0,
      this.allowanceUnits -
        (this.scanner.cumulativeMilliunits[next] - base) / CAPTION_UNIT_GRAIN
    )
    this.lastRevealedLength = next
    return next - revealed
  }

  // Sentence-boundary hold (gated windows only): the first frontier the
  // reveal may not cross — one past the first sentence end the estimated
  // audio position hasn't itself passed. Within a sentence the lead applies
  // as always; ACROSS a sentence end it used to buy exactly the
  // mid-message-break artifact: the voice rests after "…great idea!" while
  // the caption, lead ahead by construction, has already popped the opening
  // of "Here are…". The silence-onset resync pins the estimate to the stop
  // the voice is resting at, so the hold parks the caption there for the
  // break's whole length and releases within a frame of the voice resuming
  // (accrual plus crawl repayment advance the estimate at the full paced
  // rate). The boundary char itself stays revealable — the hold completes
  // "…idea!" and blocks "Here…". Ungated windows have no silence signal to
  // release against and keep the plain lead.
  private boundaryHoldFrontier(
    targetUnits: number,
    pauseUnits: number,
    rate: number
  ): number {
    const endMillis = this.scanner.sentenceEndMilliunits
    if (endMillis.length === 0) return Number.MAX_SAFE_INTEGER
    // The estimated audio position: the target minus the lead, the pause
    // debits it still carries, and whatever a crawl is holding back. Rounded
    // for the same exactness reason as the walk budget: after a resync the
    // estimate sits ON a stop's integer milliunit value, and this comparison
    // must land there deterministically, not a float crumb off.
    const audioMilli = Math.round(
      (targetUnits -
        pauseUnits -
        CAPTION_LEAD_SECONDS * rate -
        this.derateUnits -
        this.carryUnits) *
        CAPTION_UNIT_GRAIN
    )
    // Milliunit sums are non-decreasing, so the first boundary the estimate
    // hasn't strictly passed blocks everything after it; earlier boundaries
    // are all passed and hold nothing.
    let low = 0
    let high = endMillis.length
    while (low < high) {
      const mid = (low + high) >> 1
      if (endMillis[mid] < audioMilli) low = mid + 1
      else high = mid
    }
    if (low >= endMillis.length) return Number.MAX_SAFE_INTEGER
    return this.scanner.sentenceEndIndices[low] + 1
  }

  // How far the target must move so the estimated audio position (target
  // minus the constant lead) lands on the nearest resync stop, capped at
  // CAPTION_RESYNC_MAX_SECONDS forward / CAPTION_RESYNC_MAX_BACK_SECONDS back;
  // null when the scanned text has no stop to anchor to.
  private resyncDelta(targetUnits: number, rate: number): number | null {
    const stops = this.scanner.stopMilliunits
    if (stops.length === 0) return null
    const estimateMilli =
      (targetUnits - CAPTION_LEAD_SECONDS * rate - this.carryUnits) *
      CAPTION_UNIT_GRAIN
    let low = 0
    let high = stops.length
    while (low < high) {
      const mid = (low + high) >> 1
      if (stops[mid] < estimateMilli) low = mid + 1
      else high = mid
    }
    const after = low < stops.length ? stops[low] : Number.POSITIVE_INFINITY
    const before = low > 0 ? stops[low - 1] : Number.NEGATIVE_INFINITY
    const nearest =
      after - estimateMilli <= estimateMilli - before ? after : before
    if (!Number.isFinite(nearest)) return null
    // Asymmetric handling: forward rescues lag with an instant snap, but only
    // when a stop is within the trust radius; backward is repaid as a crawl
    // whose duration scales with the correction (see
    // CAPTION_RESYNC_MAX_BACK_SECONDS).
    const maxForwardMilli =
      CAPTION_RESYNC_MAX_SECONDS * rate * CAPTION_UNIT_GRAIN
    const maxBackMilli =
      CAPTION_RESYNC_MAX_BACK_SECONDS * rate * CAPTION_UNIT_GRAIN
    const rawDeltaMilli = nearest - estimateMilli
    // Nearest forward stop is beyond the trust radius: the silence is not a
    // rest-at-stop (a filler→tool gap or a short leading item's end), so
    // snapping forward would teleport the caption into unspoken text. Skip it
    // — the deficit reveal's catch-up still rescues genuine lag when audio
    // resumes. Backward is never skipped: it only ever crawls (see below), it
    // can't overshoot, and it's the anti-ahead corrector.
    if (rawDeltaMilli > maxForwardMilli) return null
    const deltaMilli = Math.max(-maxBackMilli, rawDeltaMilli)
    return deltaMilli / CAPTION_UNIT_GRAIN
  }

  // Diagnostics for the pacer heartbeat trace.
  snapshot() {
    return {
      windowKey: this.windowKey,
      windowGated: this.windowGated,
      gatedDebitPauses: this.gatedDebitPauses,
      targetUnits: this.targetUnits,
      carryUnits: this.carryUnits,
      carryPauses: this.carryPauses,
      allowanceUnits: this.allowanceUnits,
      lastRevealedLength: this.lastRevealedLength,
      scannedTextLength: this.scanner.scannedLength,
      resyncCount: this.resyncCount,
      lastResyncUnits: this.lastResyncUnits,
      forwardResyncUnits: this.forwardResyncUnits,
      resyncSkipCount: this.resyncSkipCount,
      pacedRate: this.lastPacedRate,
      derateUnits: this.derateUnits,
      speechSeconds: this.speechSeconds,
      stallCount: this.stallCount,
      // A stall still open right now counts toward the max (a window can end
      // mid-freeze), but only closed stalls increment the count.
      maxStallSeconds: Math.max(
        this.maxStallSeconds,
        this.stallSeconds >= CAPTION_STALL_SECONDS ? this.stallSeconds : 0
      ),
      maxBurstChars: this.maxBurstChars,
    }
  }
}
